<template>
  <div>
    <div>
      <WebccInput
        v-model="password"
        class="mb-2"
        name="password"
        :label="$t(`${trPrefix}password`)"
        type="password"
      />
      <WebccInput
        v-model="confirmPassword"
        class="mb-1"
        name="confirmPassword"
        :label="$t(`${trPrefix}confirmPassword`)"
        type="password"
        :valid="passwordValidity"
        @keyup.enter.stop="submit"
      />
      <div
        class="flex w-full items-center"
        :class="errorMessage ? 'mb-4' : 'mb-6'"
      >
        <template v-if="confirmPassword.length === 0 || doPasswordsMatch">
          <WebccIcon
            v-if="password.length > 7"
            name="site/check"
            class="size-4 mr-1 text-suc"
          />
          <WebccIcon
            v-else-if="password.length > 0 && password.length < 8"
            name="site/error-warning"
            class="size-4 mr-1 text-err"
          />
          <WebccIcon
            v-else
            name="site/ellipse"
            class="size-1 mr-1 text-txt-weak"
          />
        </template>
        <span
          v-if="!doPasswordsMatch && confirmPassword.length > 0"
          class="text-xs text-err"
        >
          {{ $t(`${trPrefix}noMatch`) }}
        </span>
        <span
          v-else
          class="text-xs"
          :class="
            password.length > 0 && password.length < 8
              ? 'text-err'
              : 'text-txt-weak'
          "
        >
          {{ $t(`${trPrefix}passwordHint`) }}
        </span>
      </div>

      <WebccFeedbackMessage
        v-if="errorMessage"
        type="error"
        :message="errorMessage"
        class="mb-6"
      />

      <WebccButton
        class="mb-4 w-full"
        variant="theme"
        size="lg"
        :loading
        @click="submit"
      >
        {{ $t(`${trPrefix}create`) }}</WebccButton
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
const trPrefix = 'components.auth.register.registerFormPassword.'

const props = defineProps<{ email: string }>()

const emit = defineEmits<{
  registered: [success: boolean]
}>()

const { getErrorMessage } = useAuthError(trPrefix)
const auth = useAuthStore()
const user = useUserStore()

const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const errorMessage = ref<string>()

onMounted(() => {
  document.getElementById('password')?.focus()
})

onUnmounted(() => {
  loading.value = false
  errorMessage.value = undefined
  password.value = ''
  confirmPassword.value = ''
})

async function submit() {
  if (!passwordValidity.value) return

  try {
    loading.value = true

    await auth.registerWithPassword(props.email, password.value)
    await user.registerUser(props.email)

    emit('registered', true)
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
  } finally {
    loading.value = false
  }
}

const doPasswordsMatch = computed(
  () => password.value === confirmPassword.value,
)

/**
 * Set the validity status of the confirmPassword input field.
 * If it is not filled reset the validity to undefined.
 */
const passwordValidity = computed(() => {
  if (!password.value.length || !confirmPassword.value.length) return undefined

  return doPasswordsMatch.value
})
</script>
